import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignedImage from '@/components/ipe/SignedImage';
import { modelTryOnAPI } from '@/utils/api';
import {
  buildTryOnPresetDisplayUrl,
  isTryOnSystemPresetAssetUrl,
  resolveTryOnPresetPngFallbackUrl,
} from '@/pages/ipe/workbench/constants/modelTryOnPresetConfig';
import {
  buildModelEthnicityPresetDisplayUrl,
  isModelEthnicitySystemPresetAssetUrl,
} from '@/pages/ipe/workbench/constants/modelEthnicityPresetConfig';
import { useTryOnPresets } from '@/pages/ipe/workbench/hooks/useTryOnPresets';
import {
  buildTryOnPresetDisplaySlots,
  filterSelectableTryOnPresets,
  resolveTryOnPresetCardDisplaySrc,
  shouldFetchTryOnPresetRemoteBlob,
} from '@/pages/ipe/workbench/utils/tryOnSystemModelPresetFallback';

function TryOnPresetCard({
  slot,
  modelType,
  garmentType,
  selectedPresetId,
  disabled,
  onSelect,
}) {
  const [remoteBlobUrl, setRemoteBlobUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [staticSrcOverride, setStaticSrcOverride] = useState('');
  const { t } = useTranslation('workbench');
  const preset = slot;
  const isSelected = !disabled && !slot.isEmpty && String(selectedPresetId) === String(preset.id);
  const label = slot.ethnicityLabel
    || t('modelPresetPicker.systemModelIndex', { index: slot.slotIndex || (slot.presetIndex ?? 0) + 1 });
  const isStaticPreset = preset?.source === 'static'
    || isTryOnSystemPresetAssetUrl(preset?.url)
    || isModelEthnicitySystemPresetAssetUrl(preset?.url);
  const staticDisplaySrc = useMemo(() => {
    if (!isStaticPreset) return '';
    return buildModelEthnicityPresetDisplayUrl(preset) || buildTryOnPresetDisplayUrl(preset);
  }, [isStaticPreset, preset]);
  const directDisplaySrc = useMemo(
    () => resolveTryOnPresetCardDisplaySrc(preset),
    [preset],
  );

  useEffect(() => {
    setStaticSrcOverride('');
    setFailed(false);
  }, [staticDisplaySrc, preset?.url, preset?.id]);

  useEffect(() => {
    if (slot.isEmpty || !shouldFetchTryOnPresetRemoteBlob(preset)) {
      setRemoteBlobUrl('');
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = '';

    (async () => {
      try {
        const blob = await modelTryOnAPI.fetchPresetImageBlob({
          id: preset.id,
          modelType,
          garmentType,
          assetRev: preset.assetRev,
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setRemoteBlobUrl(objectUrl);
        setFailed(false);
      } catch {
        if (!cancelled) {
          setRemoteBlobUrl('');
          setFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [slot.isEmpty, preset, modelType, garmentType]);

  const displaySrc = staticSrcOverride || staticDisplaySrc || directDisplaySrc || remoteBlobUrl;
  const cardDisabled = disabled || slot.isEmpty || !slot.selectable;

  return (
    <button
      type="button"
      className={`model-studio__preset-card${isSelected ? ' is-selected' : ''}${slot.isEmpty || failed ? ' is-empty' : ''}`}
      onClick={() => {
        if (cardDisabled || slot.isEmpty) return;
        onSelect?.(isSelected ? null : preset);
      }}
      disabled={cardDisabled}
      aria-pressed={isSelected}
      aria-label={label}
      title={label}
    >
      {displaySrc && !failed && !slot.isEmpty ? (
        isStaticPreset ? (
          <img
            src={displaySrc}
            alt={label}
            loading="lazy"
            onError={() => {
              const fallback = resolveTryOnPresetPngFallbackUrl(displaySrc);
              if (fallback && fallback !== displaySrc && staticSrcOverride !== fallback) {
                setStaticSrcOverride(fallback);
                return;
              }
              setFailed(true);
            }}
          />
        ) : (
          <SignedImage
            url={preset?.apiUrl || ''}
            displayUrl={preset?.displayUrl || preset?.viewUrl || directDisplaySrc || remoteBlobUrl}
            viewUrl={preset?.viewUrl}
            alt={label}
            loading="lazy"
            onError={() => setFailed(true)}
          />
        )
      ) : null}
      {slot.isEmpty || failed ? (
        <span className="model-studio__preset-empty-card">{label}</span>
      ) : null}
      <span className="model-studio__preset-tag">{label}</span>
    </button>
  );
}

/** 节点 12.1–12.6：系统模特选择器 — API+fallback；与用户上传模特互斥 */
export default function ModelTryOnPresetPicker({
  modelType,
  garmentType,
  selectedPresetId = '',
  disabled = false,
  paused = false,
  onSelect,
  presets: presetsProp,
  presetsLoading,
  presetsFallbackUsed,
  ethnicity,
}) {
  const { t } = useTranslation('workbench');
  const shouldFetch = presetsProp === undefined;
  const {
    presets: fetchedPresets,
    loading: fetchedLoading,
    fallbackUsed: fetchedFallbackUsed,
  } = useTryOnPresets(
    shouldFetch ? modelType : '',
    shouldFetch ? garmentType : '',
    shouldFetch ? ethnicity : undefined,
  );
  const presets = shouldFetch ? fetchedPresets : presetsProp;
  const loading = shouldFetch ? fetchedLoading : Boolean(presetsLoading);
  const fallbackUsed = shouldFetch ? fetchedFallbackUsed : Boolean(presetsFallbackUsed);
  const selectorPaused = paused || disabled;

  const displaySlots = useMemo(
    () => buildTryOnPresetDisplaySlots(modelType, garmentType, presets),
    [modelType, garmentType, presets],
  );
  const selectablePresets = useMemo(
    () => filterSelectableTryOnPresets(displaySlots),
    [displaySlots],
  );

  return (
    <div
      className={`model-studio__preset-picker${selectorPaused ? ' is-paused' : ''}`}
      aria-disabled={selectorPaused || undefined}
    >
      <div className="model-studio__preset-head">
        <span className="img2img-studio__field-label model-studio__preset-title">{t('modelPresetPicker.systemModel')}</span>
        <span className="model-studio__preset-hint">
          {selectorPaused
            ? t('modelPresetPicker.pausedHint')
            : `${t('modelPresetPicker.tryOnHint')}${fallbackUsed ? t('modelPresetPicker.staticFallbackSuffix') : ''}`}
        </span>
      </div>

      {selectorPaused ? (
        <p className="model-studio__preset-paused-note">{t('modelPresetPicker.pausedNote')}</p>
      ) : null}

      {!selectorPaused && loading ? (
        <p className="model-studio__preset-empty">{t('modelPresetPicker.loading')}</p>
      ) : null}

      {!selectorPaused && !loading && selectablePresets.length === 0 ? (
        <p className="model-studio__preset-empty">{t('modelPresetPicker.emptyTryOn')}</p>
      ) : null}

      {!loading && displaySlots.length > 0 ? (
        <div className="model-studio__preset-grid">
          {displaySlots.map((slot) => (
            <TryOnPresetCard
              key={`${slot.id}-${slot.slotIndex}`}
              slot={slot}
              modelType={modelType}
              garmentType={garmentType}
              selectedPresetId={selectorPaused ? '' : selectedPresetId}
              disabled={selectorPaused}
              onSelect={onSelect}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export { TryOnPresetCard };
